import { type Node } from "@tiptap/pm/model";
import { Plugin, TextSelection } from "@tiptap/pm/state";
import type { Range } from "@tiptap/react";
import { Extension } from "@tiptap/react";
import type { MarkdownParser, MarkdownSerializer } from "prosemirror-markdown";

import Bugsnag from "@bugsnag/js";
import ToastService from "@services/toast.service";
import { ERROR_STRINGS, ERROR_MESSAGES } from "@config/messages";
import { getParser } from "./parser";
import { getSerializer } from "./serializer";
import type { InsertMarkdownContext } from "./utils/handleInsert";
import { handleDefaultContent, handleHeading, handleList } from "./utils/handleInsert";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    markdown: {
      /**
       * Insert markdown content at the current selection
       * @param content The markdown content to insert
       */
      insertMarkdown: (content: string) => ReturnType;
      /*
       * Inserts markdown content at a specific position in the editor.
       *
       * @param position - The range where the markdown content should be inserted, with `from` and `to` positions.
       * @param content - The Markdown string to be parsed and inserted.
       *
       * - Parses the Markdown into a format the editor can use.
       * - Handles lists and paragraphs for proper formatting.
       * - Focuses the editor after inserting the content.
       * - Logs errors and shows a notification if insertion fails.
       */
      insertMarkdownAt: (position: Range, content: string) => ReturnType;
      /**
       * Get the markdown content of the current Node
       * @param content The document to get the markdown content from
       */
      getMarkdown: (content?: Node) => () => string;
    };
  }
}

export interface MarkdownOptions {}

export interface MarkdownStorage {
  parser: MarkdownParser;
  serializer: MarkdownSerializer;
}

export const Markdown = Extension.create<MarkdownOptions, MarkdownStorage>({
  name: "markdown",

  onCreate() {
    this.storage.parser = getParser(this.editor.schema);
    this.storage.serializer = getSerializer();
  },

  addCommands() {
    return {
      insertMarkdown:
        (content) =>
        ({ commands, state }) => {
          try {
            return commands.insertMarkdownAt(
              {
                from: state.selection.from,
                to: state.selection.to,
              },
              content
            );
          } catch (error) {
            Bugsnag.notify(error as Error, (event) => {
              event.addMetadata("context", { message: ERROR_STRINGS.EDITOR_INSERT });
            });

            ToastService.showUserError(ERROR_MESSAGES.EDITOR_INSERT);

            return false;
          }
        },
      insertMarkdownAt:
        (position, content) =>
        ({ chain, editor }) => {
          try {
            const fromPos = editor.view.state.doc.resolve(position.from);
            const toPos = editor.view.state.doc.resolve(position.to);
            const selection = TextSelection.create(editor.state.doc, fromPos.pos, toPos.pos);
            const parser = this.storage.parser;
            const serializer = this.storage.serializer;

            const context: InsertMarkdownContext = {
              chain,
              parser,
              serializer,
              position,
              content,
              selection,
              fromPos,
              toPos,
            };

            return handleHeading(context) || handleList(context) || handleDefaultContent(context);
          } catch (error) {
            Bugsnag.notify(error as Error, (event) => {
              event.addMetadata("context", { message: ERROR_STRINGS.EDITOR_INSERT });
            });
            ToastService.showUserError(ERROR_MESSAGES.EDITOR_INSERT);
            return false;
          }
        },
      getMarkdown: (content) => () => {
        try {
          return this.storage.serializer.serialize(content ?? this.editor.state.doc);
        } catch (error) {
          Bugsnag.notify(error as Error, (event) => {
            event.addMetadata("context", { message: ERROR_STRINGS.EDITOR_GET });
          });

          ToastService.showUserError(ERROR_MESSAGES.EDITOR_GET);

          return "";
        }
      },
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        props: {
          clipboardTextParser: (text) => {
            const parsed = this.editor.storage.markdown.parser.parse(text) as Node;
            const slice = parsed.slice(0);
            return slice;
          },
          clipboardTextSerializer: (slice) => {
            return this.editor.storage.markdown.serializer.serialize(slice.content);
          },
        },
      }),
    ];
  },
});
